/** biome-ignore-all lint/suspicious/noExplicitAny: GeoService location shapes are dynamic */
import { Combobox } from '@rocket/ui';
import { useMemo } from 'react';

import GeoService from '../../utils/GeoService';
import Location from './Location';

type AnyData = any;

export interface BBAutocompleteProps {
  label?: string;
  /** Option objects (geo locations or {id, name} likes). */
  options: Array<AnyData>;
  /** The selected option OBJECT (or null) — same contract the MUI wrapper had. */
  value?: AnyData | null;
  onChange: (option: AnyData | null) => void;
  /**
   * Async mode: fires with the raw typed text on every keystroke (replaces the
   * old MUI `filterOptions` keystroke hack) and disables local filtering —
   * the server response IS the option list.
   */
  onSearchChange?: (text: string) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const optionValue = (option: AnyData): string => String(option?.id ?? option?.name ?? '');

const BBAutocomplete = ({
  label,
  options,
  value = null,
  onChange,
  onSearchChange,
  loading = false,
  disabled = false,
  placeholder,
}: BBAutocompleteProps) => {
  const byValue = useMemo(() => {
    const map = new Map<string, AnyData>();
    for (const option of options) {
      map.set(optionValue(option), option);
    }
    return map;
  }, [options]);

  const items = useMemo(
    () =>
      options.map((option) => ({
        value: optionValue(option),
        label: String(option?.name ?? option?.id ?? ''),
      })),
    [options]
  );

  return (
    <Combobox
      label={label}
      items={items}
      value={value ? [optionValue(value)] : []}
      disabled={disabled}
      loading={loading}
      placeholder={placeholder}
      // remote mode: server already filtered; local substring filter would
      // fight it (and hide results whose label doesn't contain the query)
      shouldFilter={!onSearchChange}
      onInputChange={onSearchChange}
      renderItem={(item) => {
        const option = byValue.get(item.value);
        return option && GeoService.isGeo(option) ? <Location location={option} /> : item.label;
      }}
      onChange={(evt) => {
        const [picked] = JSON.parse(evt.target.value) as Array<string>;
        onChange(picked ? (byValue.get(picked) ?? null) : null);
      }}
    />
  );
};

export default BBAutocomplete;
